import { useEffect, useRef, useState, type FormEvent } from "react";
import { MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { Link, useLocation, useSearch } from "wouter";
import type { AlertSearch, PublicJob } from "../../shared/types.ts";
import { Button, Card, EmptyState, Field, Highlight, Input, List, ListRow, Logo, MarkLoader, Monogram, cn, useToast } from "../ui/index.ts";
import { ApiError, ERROR_TEXT, publicJobs, subscribeAlert, webUrl } from "../lib/api.ts";
import { place, posted } from "../lib/format.ts";
import { Footer } from "../landing/Closing.tsx";
import { wrap } from "../landing/styles.ts";
import { SearchField } from "./Jobs.tsx";

/** A search as its emails and the edit page name it: "Software Engineer Intern in San Francisco, CA". */
const searchLabel = (s: AlertSearch) => (s.location ? `${s.q} in ${s.location}` : s.q);

/** The `/jobs` query string for a search, empty values dropped, so an email link lands on the same results. */
export function jobsQuery(q: string, location: string): string {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (location) p.set("location", location);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** A plain sentence for a failed public call, by status. */
function failText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 429) return "Too many tries. Wait a minute and try again.";
  if (e instanceof ApiError && e.status === 503) return "This isn't available right now. Try again soon.";
  if (e instanceof TypeError) return ERROR_TEXT.api_unreachable!;
  return fallback;
}

/**
 * Signed-out `/jobs`: a role and location search over recent postings, each opening on the company's site, a job alert
 * signup for the search, and a way into headcount. The query string holds the search, so alert emails link back to it;
 * `?kept=1`, from an email's keep-sending link, says the alerts will keep coming.
 */
export function PublicJobs() {
  const [, navigate] = useLocation();
  const params = new URLSearchParams(useSearch());
  const q = (params.get("q") ?? "").trim().slice(0, 80);
  const where = (params.get("location") ?? "").trim().slice(0, 80);
  const kept = params.get("kept") === "1";
  const { toast } = useToast();
  const [items, setItems] = useState<PublicJob[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const req = useRef(0);

  useEffect(() => {
    const n = ++req.current;
    setItems(null);
    setFailed(null);
    publicJobs({ q, location: where }).then(
      (r) => {
        if (n !== req.current) return;
        setItems(r.items);
        setCursor(r.nextCursor);
      },
      (e) => n === req.current && setFailed(failText(e, "Jobs didn't load. Try again.")),
    );
  }, [q, where, attempt]);

  useEffect(() => {
    if (!kept) return;
    toast("You'll keep getting job alerts.", "ok");
    navigate(`/jobs${jobsQuery(q, where)}`, { replace: true });
  }, [kept, q, where, navigate, toast]);

  const loadMore = () => {
    const n = req.current;
    setMore(true);
    publicJobs({ q, location: where, cursor })
      .then((r) => {
        if (n !== req.current) return;
        setItems((xs) => [...(xs ?? []), ...r.items.filter((x) => !xs?.some((y) => y.id === x.id))]);
        setCursor(r.nextCursor);
      })
      .catch((e) => n === req.current && toast(failText(e, "More jobs didn't load. Try again."), "bad"))
      .finally(() => setMore(false));
  };

  const search: AlertSearch | null = q.length >= 2 ? { q, location: where || null } : null;
  const now = Date.now();

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <header className="border-b border-line pt-safe">
        <div className={cn(wrap, "flex h-16 items-center justify-between gap-3")}>
          <Link href="/" aria-label="headcount home" className="rounded-control">
            <Logo size={22} />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")}>
              Log in
            </Button>
            <Button size="sm" onClick={() => navigate("/signup")}>
              Start applying
            </Button>
          </div>
        </div>
      </header>
      <main className={cn(wrap, "flex-1 pb-16 pt-10 md:pt-14")}>
        <h1 className="text-balance font-display text-[36px] leading-[1.04] tracking-[-0.045em] text-ink md:text-[52px]">
          Find your next <Highlight>job</Highlight>.
        </h1>
        <SearchForm q={q} location={where} onSearch={(a, b) => navigate(`/jobs${jobsQuery(a, b)}`)} />
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
          <section aria-label="Jobs" className="flex min-w-0 flex-col gap-4">
            {failed ? (
              <EmptyState
                title={failed}
                action={
                  <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
                    Try again
                  </Button>
                }
              />
            ) : !items ? (
              <MarkLoader label="Searching open jobs" />
            ) : !items.length && !cursor ? (
              <EmptyState art="search" title="No jobs match that search." text="Try a different role or location." />
            ) : (
              <>
                {items.length ? (
                  <List>
                    {items.map((j) => (
                      <JobRow key={j.id} job={j} now={now} />
                    ))}
                  </List>
                ) : (
                  <EmptyState compact art="search" title="No jobs match that search in the newest postings." text="Load more to look through older ones." />
                )}
                {cursor && (
                  <Button variant="secondary" className="self-center" loading={more} onClick={loadMore}>
                    Load more jobs
                  </Button>
                )}
              </>
            )}
          </section>
          {/* Phones put the alert card above the results: `contents` makes it a grid item that `order-first` can move. */}
          <aside className="contents lg:block">
            <div className="contents lg:sticky lg:top-6 lg:flex lg:flex-col lg:gap-4">
              <div className="order-first lg:order-none">
                <AlertCard key={search ? searchLabel(search) : ""} search={search} />
              </div>
              <div className="rounded-card bg-primary p-5 text-on-primary">
                <h2 className="font-display text-[22px] leading-[1.1] tracking-[-0.03em]">Let headcount apply for you.</h2>
                <p className="mt-1.5 text-pretty text-[14px] text-white/80">Create a free account and headcount applies to jobs like these for you.</p>
                <Button variant="secondary" className="mt-4 w-full text-primary-ink ring-0 hover:bg-cream-soft" onClick={() => navigate("/signup")}>
                  Create free account
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <div className="border-t border-line pt-4">
        <Footer />
      </div>
    </div>
  );
}

function SearchForm({ q, location, onSearch }: { q: string; location: string; onSearch: (q: string, location: string) => void }) {
  const [text, setText] = useState(q);
  const [where, setWhere] = useState(location);
  useEffect(() => setText(q), [q]);
  useEffect(() => setWhere(location), [location]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(text.trim(), where.trim());
  };

  return (
    <form
      role="search"
      onSubmit={submit}
      className="mt-6 flex max-w-[880px] flex-col gap-1 rounded-card bg-surface p-1.5 shadow-raised transition-[box-shadow] duration-150 focus-within:shadow-[0_0_0_2px_var(--primary)] sm:flex-row sm:items-center md:mt-8"
    >
      <SearchField
        icon={<MagnifyingGlass weight="bold" />}
        value={text}
        onChange={setText}
        onClear={() => onSearch("", where.trim())}
        placeholder="Title, company or skill"
        label="Role"
        className="min-w-0 sm:flex-[1.4]"
      />
      <span aria-hidden className="mx-3 h-px shrink-0 bg-line sm:mx-0 sm:h-7 sm:w-px sm:bg-line-strong" />
      <SearchField
        icon={<MapPin weight="bold" />}
        value={where}
        onChange={setWhere}
        onClear={() => onSearch(text.trim(), "")}
        placeholder="City, state or remote"
        label="Location"
        className="min-w-0 sm:flex-1"
      />
      <Button type="submit" className="mt-1 h-11 sm:mt-0 sm:h-10 sm:px-6">
        Search
      </Button>
    </form>
  );
}

/** One posting. The row opens it on the company's site in a new tab. */
function JobRow({ job, now }: { job: PublicJob; now: number }) {
  const href = webUrl(job.jobUrl);
  const meta = [job.company, place({ locations: job.locations, workplace: job.workplace ?? "" }), posted(job.postedAt, now)].filter(Boolean).join(" · ");
  return (
    <ListRow
      leading={<Monogram name={job.company} url={job.jobUrl} />}
      title={<span className="line-clamp-2 whitespace-normal">{job.title}</span>}
      meta={<span className="whitespace-normal">{meta}</span>}
      onClick={href ? () => window.open(href, "_blank", "noopener,noreferrer") : undefined}
    />
  );
}

/** The job alert signup for the current search. A search under two characters can't be an alert, so the card asks for one. */
function AlertCard({ search }: { search: AlertSearch | null }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ field: "email" | "form"; text: string } | null>(null);
  const title = "text-balance font-display text-[20px] leading-[1.15] tracking-[-0.03em] text-ink";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending || !search) return;
    const to = email.trim();
    if (!to) return setProblem({ field: "email", text: "Enter your email." });
    setSending(true);
    setProblem(null);
    try {
      await subscribeAlert({ email: to, q: search.q, location: search.location });
      setSentTo(to);
    } catch (err) {
      if (err instanceof ApiError && err.error === "bad_email") setProblem({ field: "email", text: "That doesn't look like an email." });
      else if (err instanceof ApiError && err.error === "bad_search") setProblem({ field: "form", text: "This search can't be saved as an alert. Try a different one." });
      else setProblem({ field: "form", text: failText(err, "Your alert wasn't set up. Try again.") });
    }
    setSending(false);
  };

  if (!search)
    return (
      <Card className="p-5">
        <h2 className={title}>Get new jobs by email.</h2>
        <p className="mt-1.5 text-[14px] text-muted">Search for a role to set up a job alert.</p>
      </Card>
    );
  if (sentTo)
    return (
      <Card className="p-5" role="status">
        <h2 className={title}>Your {searchLabel(search)} job alert is set up.</h2>
        <p className="mt-1.5 text-[14px] text-muted">
          We sent the first jobs to <span className="font-semibold text-ink-soft">{sentTo}</span>.
        </p>
      </Card>
    );
  return (
    <Card className="p-5">
      <h2 className={title}>
        Get new {search.q} jobs{search.location ? ` in ${search.location}` : ""} by email.
      </h2>
      <form className="mt-4 flex flex-col gap-3" onSubmit={submit} noValidate>
        <Field label="Email" error={problem?.field === "email" ? problem.text : undefined}>
          <Input type="email" autoComplete="email" inputMode="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {problem?.field === "form" && (
          <p className="text-[13px] text-bad-ink" role="alert">
            {problem.text}
          </p>
        )}
        <Button type="submit" loading={sending}>
          Create alert
        </Button>
      </form>
      <p className="mt-3 text-[12.5px] text-muted">You can unsubscribe at any time.</p>
    </Card>
  );
}
